import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import API from "../../api";
import { listLevels } from "../../api/levels";
import { formatLevelLabel } from "../../utils/formatLevelLabel";
import { listCoachRosterStudents, listStudents, listCoaches, listManagerActiveStudents } from "../../api/users";
import { useCurrentUser } from "../../context/UserContext";
import { DatePicker } from "../common/DatePicker";
import { showConfirm } from '../../utils/popup';
import { filterHhMmTimes } from "../../utils/timeOptions";
import { formatCurrency, getCurrencySymbol, formatCurrencyRaw } from "../../utils/currency";
import { TextInput, SelectInput, TextareaInput } from "../common/form";
import { toISODateFromRegional } from "../../utils/date";
import { formatTimeFromHhMm } from "../../utils/regionalFormat";

// Generate time options with 15-minute intervals
const generateTimeOptions = () => {
    const options = [];
    for (let h = 0; h < 24; h++) {
        for (let m = 0; m < 60; m += 15) {
            const hour = String(h).padStart(2, "0");
            const minute = String(m).padStart(2, "0");
            options.push(`${hour}:${minute}`);
        }
    }
    return options;
};

const IndividualSessionModal = ({ role, isOpen, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const currencySymbol = getCurrencySymbol();
    const { user } = useCurrentUser();
    const [form, setForm] = useState({
        studentId: "",
        levelId: "",
        coachId: "",
        sessionDate: "",
        startTime: "",
        endTime: "",
        topic: "",
        charge: "",
    });

    const allTimeOptions = useMemo(() => generateTimeOptions(), []);
    const startTimeOptions = useMemo(
        () => filterHhMmTimes(allTimeOptions, { maxTime: form.endTime || null }),
        [allTimeOptions, form.endTime]
    );
    const endTimeOptions = useMemo(
        () => filterHhMmTimes(allTimeOptions, { minTime: form.startTime || null }),
        [allTimeOptions, form.startTime]
    );

    const [levels, setLevels] = useState([]);
    const [students, setStudents] = useState([]);
    const [coaches, setCoaches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!isOpen) return;

        let alive = true;

        // Load levels, students, and coaches
        (async () => {
            try {
                // Fetch levels
                const levelsRes = await listLevels(role, { page: 0, size: 500 });
                const levelsList = levelsRes?.data?.content ?? levelsRes?.data ?? levelsRes ?? [];

                let studentsList = [];
                if (role === "COACH") {
                    studentsList = await listCoachRosterStudents();
                } else if (role === "MANAGER") {
                    studentsList = await listManagerActiveStudents();
                } else {
                    studentsList = await listStudents({ page: 0, size: 500 });
                }

                // For COACH role, use current user instead of fetching all coaches (403 error)
                let coachesList = [];
                if (role === "COACH" && user) {
                    coachesList = [user];
                } else if (role === "MANAGER") {
                    const res = await API.get("/manager/users", { params: { role: "COACH", page: 0, size: 500 } });
                    const data = res?.data;
                    coachesList = Array.isArray(data) ? data : (Array.isArray(data?.content) ? data.content : []);
                } else {
                    // For ADMIN, fetch all coaches
                    coachesList = await listCoaches({ page: 0, size: 500 });
                }

                if (!alive) return;

                setLevels(levelsList);
                setStudents(studentsList);
                setCoaches(coachesList);

                // Auto-select coach if logged in as COACH and auto-populate charge
                if (role === "COACH" && user?.id) {
                    const coach = coachesList.find(c => (c.id || c.userId) === user.id);
                    const initialCharge = coach?.defaultIndividualSessionRate || "";
                    setForm(prev => ({
                        ...prev,
                        coachId: String(user.id),
                        charge: String(initialCharge)
                    }));
                }
            } catch (err) {
                console.error("Error loading data:", err);
                if (alive) {
                    setError(t('common.optionsLoadFailed'));
                }
            }
        })();

        return () => { alive = false; };
    }, [isOpen, role, user]);

    const getFullName = (user) => {
        const parts = [user.firstName, user.middleName, user.lastName].filter(Boolean);
        return parts.length > 0 ? parts.join(" ") : user.username || t('sessions.walkInModal.userFallback', { id: user.id || user.userId });
    };

    // Calculate charge based on hourly rate and duration
    const calculateChargeForDuration = (hourlyRate, startTime, endTime) => {
        if (!hourlyRate || !startTime || !endTime) return hourlyRate || "";

        const duration = calculateDuration(startTime, endTime);
        if (duration <= 0) return hourlyRate;

        const calculatedCharge = formatCurrencyRaw(parseFloat(hourlyRate) * duration);
        return calculatedCharge;
    };

    const clearEndIfStartInvalid = (prev, startTime) =>
        prev.endTime && startTime && prev.endTime <= startTime ? "" : prev.endTime;

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Handle coach selection - update charge based on duration
        if (name === "coachId") {
            if (value) {
                const selectedCoach = coaches.find(coach => String(coach.id) === value || String(coach.userId) === value);
                if (selectedCoach && selectedCoach.defaultIndividualSessionRate) {
                    const calculatedCharge = calculateChargeForDuration(
                        selectedCoach.defaultIndividualSessionRate,
                        form.startTime,
                        form.endTime
                    );
                    setForm((prev) => ({
                        ...prev,
                        [name]: value,
                        charge: String(calculatedCharge)
                    }));
                    return;
                } else {
                    setForm((prev) => ({
                        ...prev,
                        [name]: value,
                        charge: ""
                    }));
                    return;
                }
            } else {
                setForm((prev) => ({
                    ...prev,
                    [name]: value,
                    charge: ""
                }));
                return;
            }
        }

        // Handle time changes - recalculate charge if coach has default rate
        if (name === "startTime" || name === "endTime") {
            const newStartTime = name === "startTime" ? value : form.startTime;
            const newEndTime = name === "endTime" ? value : form.endTime;

            if (form.coachId && newStartTime && newEndTime) {
                const selectedCoach = coaches.find(coach =>
                    String(coach.id) === form.coachId || String(coach.userId) === form.coachId
                );

                if (selectedCoach && selectedCoach.defaultIndividualSessionRate) {
                    const calculatedCharge = calculateChargeForDuration(
                        selectedCoach.defaultIndividualSessionRate,
                        newStartTime,
                        newEndTime
                    );
                    setForm((prev) => ({
                        ...prev,
                        [name]: value,
                        endTime: name === "startTime" ? clearEndIfStartInvalid(prev, value) : prev.endTime,
                        charge: String(calculatedCharge)
                    }));
                    return;
                }
            }
        }

        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (value) => {
        setForm((prev) => ({ ...prev, sessionDate: value }));
    };

    const calculateDuration = (startTime, endTime) => {
        const [startHour, startMin] = startTime.split(':').map(Number);
        const [endHour, endMin] = endTime.split(':').map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;
        return (endMinutes - startMinutes) / 60; // duration in hours
    };

    const formatDuration = (hours) => {
        const h = Math.floor(hours);
        const m = Math.round((hours - h) * 60);
        if (m === 0) return t('sessions.form.hour', { count: h });
        return `${t('sessions.form.hour', { count: h })} ${t('sessions.form.minute', { count: m })}`;
    };

    const formatSessionTimeLabel = (time24) => formatTimeFromHhMm(time24);

    const validateForm = () => {
        if (!form.levelId) return t('sessions.individualModal.selectLevelError');
        if (!form.studentId) return t('sessions.individualModal.selectStudentError');
        if (!form.coachId) return t('sessions.individualModal.selectCoachError');
        if (!form.sessionDate) return t('sessions.individualModal.selectDateError');
        if (!form.startTime) return t('sessions.individualModal.selectStartError');
        if (!form.endTime) return t('sessions.individualModal.selectEndError');
        if (!form.topic.trim()) return t('sessions.individualModal.topicRequired');
        if (!form.charge || Number(form.charge) <= 0) return t('sessions.individualModal.chargeRequired');
        if (form.endTime <= form.startTime) return t('sessions.individualModal.endAfterStart');
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        // Check duration and confirm if > 3 hours
        const duration = calculateDuration(form.startTime, form.endTime);
        if (duration > 3) {
            const confirmed = await showConfirm(
                `⚠️ ${t('sessions.individualModal.longSessionTitle')}\n\n` +
                `${t('sessions.individualModal.sessionDateLabel')} ${form.sessionDate}\n` +
                `${t('sessions.individualModal.startTimeLabel')} ${formatSessionTimeLabel(form.startTime)}\n` +
                `${t('sessions.individualModal.endTimeLabel')} ${formatSessionTimeLabel(form.endTime)}\n` +
                `${t('sessions.individualModal.durationLabel')} ${formatDuration(duration)}\n\n` +
                t('sessions.individualModal.longSessionProceed')
            );
            if (!confirmed) {
                return;
            }
        }

        setLoading(true);
        setError("");

        try {
            const isoDate = toISODateFromRegional(form.sessionDate);
            if (!isoDate) {
                setError(t('sessions.individualModal.invalidDate'));
                setLoading(false);
                return;
            }

            // Combine date + time to ISO format (YYYY-MM-DDTHH:mm)
            const startTimeISO = `${isoDate}T${form.startTime}`;
            const endTimeISO = `${isoDate}T${form.endTime}`;

            const payload = {
                levelId: Number(form.levelId),
                studentId: Number(form.studentId),
                coachId: Number(form.coachId),
                startTime: startTimeISO,
                endTime: endTimeISO,
                topic: form.topic.trim(),
                charge: Number(form.charge),
            };

            const base = role === "ADMIN" ? "/admin" : role === "MANAGER" ? "/manager" : "/coach";
            const response = await API.post(`${base}/sessions/individual`, payload);

            // Reset form
            setForm({
                studentId: "",
                levelId: "",
                coachId: role === "COACH" && user?.id ? String(user.id) : "",
                sessionDate: "",
                startTime: "",
                endTime: "",
                topic: "",
                charge: "",
            });

            if (onSuccess) {
                onSuccess(response.data);
            }

            onClose();
        } catch (err) {
            console.error("Error creating individual session:", err);
            setError(apiErrorMessage(err, t('sessions.individualModal.createFailed')));
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setForm({
            studentId: "",
            levelId: "",
            coachId: role === "COACH" && user?.id ? String(user.id) : "",
            sessionDate: "",
            startTime: "",
            endTime: "",
            topic: "",
            charge: "",
        });
        setError("");
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header with gradient and close button */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-5 rounded-t-xl flex justify-between items-center sticky top-0 z-10">
                    <h2 className="text-xl font-bold text-white">👤 {t('sessions.individualModal.title')}</h2>
                    <button
                        type="button"
                        onClick={handleCancel}
                        className="text-white hover:bg-white/20 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
                        disabled={loading}
                    >
                        ✕
                    </button>
                </div>

                <div className="p-5">
                    {/* Informational note about automatic actions */}
                    <div className="mb-4 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg">
                        <div className="flex items-start">
                            <svg className="w-4 h-4 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <div className="text-xs text-indigo-800">
                                <p className="font-semibold mb-1">{t('sessions.individualModal.autoActions')}</p>
                                <ul className="list-disc list-inside space-y-0.5 ml-1 text-xs">
                                    <li>{t('sessions.individualModal.autoPresent')}</li>
                                    <li>{t('sessions.individualModal.autoCharge')}</li>
                                    <li>{t('sessions.individualModal.autoCompleted')}</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-3 p-2.5 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-3">
                        {/* Session Details Section */}
                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-3 rounded-lg border border-indigo-100 space-y-2.5">
                            <h3 className="text-xs font-semibold text-indigo-900 mb-2">📋 {t('sessions.individualModal.sessionDetails')}</h3>

                            <SelectInput
                                id="levelId"
                                label={t('sessions.level')}
                                name="levelId"
                                value={form.levelId}
                                onChange={handleChange}
                                className="text-xs"
                                required
                            >
                                <option value="">{t('sessions.individualModal.selectLevel')}</option>
                                {levels.map((level) => (
                                    <option key={level.id || level.levelId} value={level.id || level.levelId}>
                                        {formatLevelLabel(level)}
                                    </option>
                                ))}
                            </SelectInput>

                            <SelectInput
                                id="studentId"
                                label={t('sessions.individualModal.studentName')}
                                name="studentId"
                                value={form.studentId}
                                onChange={handleChange}
                                className="text-xs"
                                required
                            >
                                <option value="">{t('sessions.individualModal.selectStudent')}</option>
                                {students.map((student) => {
                                    const studentId = student.id || student.userId;
                                    const walletBalance = student.walletBalance ?? student.wallet_balance ?? null;
                                    return (
                                        <option key={studentId} value={studentId}>
                                            {getFullName(student)}
                                            {walletBalance != null ? ` (${formatCurrency(walletBalance)})` : ''}
                                        </option>
                                    );
                                })}
                            </SelectInput>

                            <SelectInput
                                id="coachId"
                                label={t('sessions.individualModal.sessionCoach')}
                                name="coachId"
                                value={form.coachId}
                                onChange={handleChange}
                                className="text-xs"
                                required
                                disabled={role === "COACH"}
                            >
                                <option value="">{t('sessions.individualModal.selectCoach')}</option>
                                {coaches.map((coach) => {
                                    const coachId = coach.id || coach.userId;
                                    return (
                                        <option key={coachId} value={coachId}>
                                            {getFullName(coach)}
                                        </option>
                                    );
                                })}
                            </SelectInput>
                        </div>

                        {/* Timing Section */}
                        <div className="bg-gradient-to-r from-blue-50 to-sky-50 p-3 rounded-lg border border-blue-100 space-y-2.5">
                            <h3 className="text-xs font-semibold text-blue-900 mb-2">⏰ {t('sessions.individualModal.timing')}</h3>

                            <div>
                                <label htmlFor="sessionDate" className="block text-xs font-medium text-blue-900 mb-1">
                                    {t('sessions.individualModal.sessionDate')} <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                    value={form.sessionDate}
                                    onChange={handleDateChange}
                                    className="w-full text-xs"
                                />
                            </div>

                            {/* Time Fields */}
                            <div className="grid grid-cols-2 gap-2.5">
                                {/* Start Time */}
                                <SelectInput
                                    id="startTime"
                                    label={t('sessions.individualModal.startTime')}
                                    name="startTime"
                                    value={form.startTime}
                                    onChange={handleChange}
                                    className="text-xs"
                                    required
                                >
                                    <option value="">{t('sessions.individualModal.selectTime')}</option>
                                    {startTimeOptions.map((time) => (
                                        <option key={time} value={time}>
                                            {formatSessionTimeLabel(time)}
                                        </option>
                                    ))}
                                </SelectInput>

                                <SelectInput
                                    id="endTime"
                                    label={t('sessions.individualModal.endTime')}
                                    name="endTime"
                                    value={form.endTime}
                                    onChange={handleChange}
                                    className="text-xs"
                                    required
                                >
                                    <option value="">{t('sessions.individualModal.selectTime')}</option>
                                    {endTimeOptions.map((time) => (
                                        <option key={time} value={time}>
                                            {formatSessionTimeLabel(time)}
                                        </option>
                                    ))}
                                </SelectInput>
                            </div>
                        </div>

                        {/* Topic Section */}
                        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-3 rounded-lg border border-purple-100">
                            <TextareaInput
                                id="topic"
                                label={t('sessions.individualModal.sessionTopic')}
                                name="topic"
                                value={form.topic}
                                onChange={handleChange}
                                rows={3}
                                placeholder={t('sessions.individualModal.topicPlaceholder')}
                                className="text-xs"
                                required
                            />
                        </div>

                        {/* Payment Section */}
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-3 rounded-lg border border-green-100">
                            <TextInput
                                id="charge"
                                label={t('sessions.individualModal.sessionCharge', { symbol: currencySymbol })}
                                type="number"
                                name="charge"
                                value={form.charge}
                                onChange={handleChange}
                                min="0"
                                step="0.01"
                                placeholder={t('sessions.individualModal.chargePlaceholder')}
                                className="text-xs"
                                required
                                note={t('sessions.individualModal.chargeNote')}
                            />
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-end space-x-2.5 pt-3 border-t">
                            <button
                                type="button"
                                onClick={handleCancel}
                                disabled={loading}
                                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-xs font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {loading ? t('sessions.individualModal.creating') : t('sessions.individualModal.create')}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default IndividualSessionModal;
